import React, { useEffect, useState, cloneElement, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Sparkles, HashIcon, FileTextIcon, CheckIcon, MailIcon, CalendarCheckIcon, PlusIcon, SmileIcon, SendHorizonalIcon, WifiIcon, XIcon } from 'lucide-react';
// "Works where you work" visual — the BOTTOM HALF of a real iPhone (the device
// is anchored to the bottom of the card and runs off the top edge, so only the
// lower portion of the phone is in frame: the rounded bottom rail, the home
// indicator, and the lower part of the screen). On the screen, a Slack channel
// shows Faith typing a message to @Giga, sending it, and Giga replying — a tiny
// looping scene so it reads as a live phone, not a static mock.
// Authentic four-color Slack mark, inlined as SVG so the header reads
// unmistakably as Slack (the CDN only serves a flat monochrome version).
function SlackMark({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" className={className} aria-hidden="true" data-id="element-531">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" fill="#E01E5A" data-id="element-532" />
      
      <path d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.527 2.527 0 0 1 2.521 2.521 2.527 2.527 0 0 1-2.521 2.521H2.522A2.527 2.527 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" fill="#36C5F0" data-id="element-533" />
      
      <path d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522v-2.521zm-1.272 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.161 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" fill="#2EB67D" data-id="element-534" />
      
      <path d="M15.161 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.161 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.272a2.527 2.527 0 0 1-2.52-2.521 2.526 2.526 0 0 1 2.52-2.522h6.317A2.527 2.527 0 0 1 24 15.163a2.528 2.528 0 0 1-2.522 2.521h-6.317z" fill="#ECB22E" data-id="element-535" />
      
    </svg>;
}
function WorksWhereYouWorkVisual() {
  const reduced = !!useReducedMotion();
  // A TWO-PERSON conversation with Giga. Faith's ask is ALREADY in the thread
  // when the scene opens (no blank starting state) — the live action is a
  // teammate, Marcus, typing his follow-up into the composer, sending it, and
  // then Giga handling both asks. Shows Giga working with a whole team in one
  // channel, not just answering a single person.
  // Scene steps:
  // 0 — Marcus typing his follow-up in the composer (Faith's message already sent)
  // 1 — Marcus's message sent into the thread
  // 2 — Giga "typing" indicator
  // 3 — Giga's reply lands, handling both asks
  const [step, setStep] = useState(reduced ? 3 : 0);
  // Faith's opening ask — static, always shown from the first frame.
  const FAITH_MSG = 'find 30 min we\u2019re all free this week and send the invite';
  // Marcus's follow-up — the text that types out live in the composer.
  const MARCUS_TYPED = "and book a room if one's free \uD83D\uDE4F";
  const [typedLen, setTypedLen] = useState(reduced ? MARCUS_TYPED.length : 0);
  // Drive the looping scene.
  useEffect(() => {
    if (reduced) return;
    let timers: ReturnType<typeof setTimeout>[] = [];
    let typeInterval: ReturnType<typeof setInterval> | undefined;
    const run = () => {
      setStep(0);
      setTypedLen(0);
      // Type Marcus's follow-up out character by character in the composer.
      let i = 0;
      typeInterval = setInterval(() => {
        i += 1;
        setTypedLen(i);
        if (i >= MARCUS_TYPED.length) {
          clearInterval(typeInterval);
          // Hold the finished text briefly, then send Marcus's message.
          timers.push(setTimeout(() => setStep(1), 450));
          // Giga starts "typing"…
          timers.push(setTimeout(() => setStep(2), 1250));
          // …then replies, handling both asks.
          timers.push(setTimeout(() => setStep(3), 2050));
          // Hold the full conversation — longer now, so the richer reply and
          // its action checklist have time to read — then loop.
          timers.push(setTimeout(run, 7200));
        }
      }, 30);
    };
    timers.push(setTimeout(run, 600));
    return () => {
      timers.forEach(clearTimeout);
      if (typeInterval) clearInterval(typeInterval);
    };
  }, [reduced]);
  const marcusSent = step >= 1;
  const gigaTyping = step === 2;
  const gigaReplied = step >= 3;
  return <div className="absolute inset-0 overflow-hidden" data-id="element-536">
      {/* The phone is built at REAL iPhone logical dimensions (390 × 800 — the
              same 19.5:9 footprint as a real device) so every element inside — bezel
              thickness, text size, avatars, the Slack header — is sized exactly as it
              would be on an actual phone. We then UNIFORMLY transform-scale the whole
              device down to fit the card (smaller on mobile, larger on desktop). A
              uniform scale keeps all proportions perfectly true-to-life: it reads as
              a faithful miniature of a real iPhone, never a distorted "toy phone"
              where the text and chrome are oversized relative to the frame. The phone
              is taller than the card, so the bottom crops off the frame while the
              Slack header stays pinned in view.
                 The scaler is absolutely positioned and centered with translate so its
              unscaled 390px box never breaks centering or overflows the card on
              narrow screens — only the visual transform changes per breakpoint. */}
      <div className="absolute top-4 md:top-6 left-1/2 origin-top -translate-x-1/2 scale-[0.58] sm:scale-[0.66] md:scale-[0.82] lg:scale-[0.9]" data-id="element-537">
        <div className="relative h-[800px] w-[390px] rounded-[3.6rem] p-[4px] shadow-[0_18px_50px_-16px_rgba(43,33,26,0.55)]" style={{
        background: 'linear-gradient(150deg, #5A5A5E 0%, #232326 28%, #050505 60%, #34343A 100%)'
      }} data-id="element-538">
          
          {/* Inner black bezel — an EVEN, thin matte-black rail on all four sides
            (uniform p-[10px]), with the screen radius kept concentric to the
            device radius so the corners read like a real iPhone rather than a
            generic rounded rectangle. */}
          <div className="relative h-full w-full rounded-[3.05rem] bg-black p-[10px]" data-id="element-539">
            {/* The screen */}
            <div className="relative h-full w-full rounded-[2.5rem] bg-[#3F0E40] overflow-hidden flex flex-col" data-id="element-540">
              {/* iOS status bar — the strongest "this is an iPhone" signal: the
                clock on the left, the Dynamic Island pill centered, and the
                cellular / Wi-Fi / battery glyphs on the right, all sitting over
                the aubergine Slack chrome. */}
              <div className="relative bg-[#3F0E40] px-6 pt-3 pb-1.5 flex items-center justify-between shrink-0" data-id="element-541">
                <span className="text-[13px] font-semibold text-white tracking-tight" data-id="element-542">
                  9:15
                </span>
                {/* Dynamic Island */}
                <span aria-hidden="true" className="absolute left-1/2 top-2 -translate-x-1/2 h-6 w-[88px] rounded-full bg-black" data-id="element-543" />
                
                <span className="flex items-center gap-1.5 text-white" aria-hidden="true" data-id="element-544">
                  
                  {/* Cellular bars */}
                  <span className="flex items-end gap-0.5 h-3" data-id="element-545">
                    <span className="w-0.5 h-1.5 rounded-sm bg-white" data-id="element-546" />
                    <span className="w-0.5 h-2 rounded-sm bg-white" data-id="element-547" />
                    <span className="w-0.5 h-2.5 rounded-sm bg-white" data-id="element-548" />
                    <span className="w-0.5 h-3 rounded-sm bg-white" data-id="element-549" />
                  </span>
                  <WifiIcon size={15} strokeWidth={2.25} className="text-white" data-id="element-550" />
                  
                  {/* Battery */}
                  <span className="relative ml-0.5 flex items-center" data-id="element-551">
                    <span className="h-3 w-6 rounded-[3px] border border-white/80 p-[1.5px] flex" data-id="element-552">
                      <span className="h-full w-[80%] rounded-[1px] bg-white" data-id="element-553" />
                    </span>
                    <span className="ml-px h-1.5 w-0.5 rounded-r-sm bg-white/80" data-id="element-554" />
                  </span>
                </span>
              </div>
              {/* Slack workspace top bar (aubergine) — the Slack mark + workspace
                name + the Slack wordmark, so it reads unmistakably as the Slack
                mobile app, not just a generic chat. Fills the space that used to
                be empty white at the top of the screen. */}
              <div className="bg-[#3F0E40] px-4 pt-1.5 pb-3 flex items-center gap-2.5 shrink-0" data-id="element-555">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-white shrink-0" data-id="element-556">
                  <SlackMark className="w-5 h-5" data-id="element-557" />
                </span>
                <div className="min-w-0 flex-1" data-id="element-558">
                  <span className="block text-base font-bold text-white leading-tight truncate" data-id="element-559">
                    Acme HQ
                  </span>
                  <span className="block text-xs text-white/55 leading-tight" data-id="element-560">
                    Slack
                  </span>
                </div>
                <SlackMark className="w-5 h-5 opacity-80 shrink-0" data-id="element-561" />
              </div>
              {/* Channel sub-header — the open channel, with a Slack-style member
                count, beneath the workspace bar. */}
              <div className="bg-[#3F0E40] border-t border-white/10 px-4 pb-3 flex items-center gap-2 shrink-0" data-id="element-562">
                <HashIcon size={16} className="text-white/70 shrink-0" data-id="element-563" />
                <span className="text-sm font-bold text-white truncate" data-id="element-564">
                  team
                </span>
                <span className="ml-auto flex items-center gap-1.5 text-xs font-semibold text-white/55 shrink-0" data-id="element-565">
                  <span className="inline-block w-2 h-2 rounded-full bg-[#2EB67D]" data-id="element-566" />
                  12
                </span>
              </div>

              {/* Thread — the device is cropped at the bottom, so messages flow from
                just under the fixed Slack header downward and scroll up as the
                conversation advances, with earlier messages clipping cleanly at
                the solid top edge beneath the header. */}
              <div className="flex-1 px-4 pt-3 pb-4 flex flex-col justify-start bg-white overflow-hidden" data-id="element-567">
                {/* As the scene advances the whole thread SCROLLS UP smoothly — like
                  a real Slack conversation scrolling so the newest message stays in
                  view: it eases up as Marcus sends, again while Giga is typing, and
                  settles once Giga's reply lands. The animated wrapper owns the
                  vertical scroll; a separate inner wrapper scales the conversation
                  DOWN on mobile (origin top-left) so the in-screen text/avatars
                  read at a realistic size on the smaller mobile device, and sits at
                  full size on desktop where the phone is larger. */}
                <motion.div animate={{
                y: reduced ? 0 : gigaReplied ? -128 : gigaTyping ? -46 : marcusSent ? -20 : 0
              }} transition={{
                type: 'spring',
                stiffness: 180,
                damping: 26
              }} data-id="element-568">
                  
                  <div className="flex flex-col gap-4" data-id="element-569">
                    {/* Faith's message — ALREADY in the thread when the scene opens,
                      so there's never a blank starting state. Rendered statically. */}
                    <div className="flex items-start gap-3" data-id="element-570">
                      <img src="/CleanShot_2026-06-22_at_12.15.002x.png" alt="Faith" className="w-11 h-11 rounded-lg object-cover shrink-0" loading="lazy" data-id="element-571" />
                      
                      <div className="min-w-0" data-id="element-572">
                        <div className="flex items-baseline gap-2" data-id="element-573">
                          <span className="text-base font-bold text-neutral-900 leading-none" data-id="element-574">
                            Faith
                          </span>
                          <span className="text-sm text-neutral-400 leading-none" data-id="element-575">
                            9:14
                          </span>
                        </div>
                        <p className="mt-2 text-base text-neutral-700 leading-snug" data-id="element-576">
                          <span className="font-semibold text-[#1264A3] bg-[#1264A3]/10 rounded px-1 py-0.5" data-id="element-577">
                            @Giga
                          </span>{' '}
                          {FAITH_MSG}
                        </p>
                      </div>
                    </div>

                    {/* Marcus's follow-up — lands in the thread once his typed
                      message is "sent", proving Giga works with the whole channel. */}
                    <AnimatePresence data-id="element-578">
                      {marcusSent && <motion.div className="flex items-start gap-3" initial={reduced ? false : {
                      opacity: 0,
                      y: 10,
                      scale: 0.96
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 320,
                      damping: 22
                    }} data-id="element-579">
                        
                          <img src="https://randomuser.me/api/portraits/men/32.jpg" alt="Marcus" className="w-11 h-11 rounded-lg object-cover shrink-0" loading="lazy" data-id="element-580" />
                        
                          <div className="min-w-0" data-id="element-581">
                            <div className="flex items-baseline gap-2" data-id="element-582">
                              <span className="text-base font-bold text-neutral-900 leading-none" data-id="element-583">
                                Marcus
                              </span>
                              <span className="text-sm text-neutral-400 leading-none" data-id="element-584">
                                9:15
                              </span>
                            </div>
                            <p className="mt-2 text-base text-neutral-700 leading-snug" data-id="element-585">
                              {MARCUS_TYPED}
                            </p>
                          </div>
                        </motion.div>}
                    </AnimatePresence>

                    {/* Giga typing indicator */}
                    <AnimatePresence data-id="element-586">
                      {gigaTyping && <motion.div className="flex items-center gap-2" initial={reduced ? false : {
                      opacity: 0,
                      y: 6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0
                    }} transition={{
                      duration: 0.25
                    }} data-id="element-587">
                        
                          <span className="w-11 h-11 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-588">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-589" />
                          
                          </span>
                          <span className="flex items-center gap-1.5 rounded-full bg-neutral-100 px-3.5 py-2.5" data-id="element-590">
                            {[0, 1, 2].map(d => <motion.span key={d} className="w-2.5 h-2.5 rounded-full bg-neutral-400" animate={{
                          opacity: [0.3, 1, 0.3]
                        }} transition={{
                          duration: 0.9,
                          repeat: Infinity,
                          delay: d * 0.18
                        }} data-id="element-591" />)}
                          </span>
                        </motion.div>}
                    </AnimatePresence>

                    {/* Giga's reply */}
                    <AnimatePresence data-id="element-592">
                      {gigaReplied && <motion.div className="flex items-start gap-3" initial={reduced ? false : {
                      opacity: 0,
                      y: 10,
                      scale: 0.96
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} transition={{
                      type: 'spring',
                      stiffness: 320,
                      damping: 22
                    }} data-id="element-593">
                        
                          <span className="w-11 h-11 rounded-lg bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-594">
                            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-6 h-6 object-contain [filter:brightness(0)_invert(1)]" data-id="element-595" />
                          
                          </span>
                          <div className="min-w-0" data-id="element-596">
                            <div className="flex items-baseline gap-2" data-id="element-597">
                              <span className="text-base font-bold text-neutral-900 leading-none" data-id="element-598">
                                Giga
                              </span>
                              <span className="text-[11px] font-bold text-[#616061] bg-neutral-200/80 rounded px-1.5 py-0.5 leading-none uppercase tracking-wide" data-id="element-599">
                                App
                              </span>
                            </div>
                            <p className="mt-2 text-base text-neutral-700 leading-snug" data-id="element-600">
                              On it. Checked all 6 calendars — the only slot
                              that clears everyone is{' '}
                              <span className="font-semibold text-neutral-900" data-id="element-601">
                                Thursday 2:00 PM
                              </span>
                              . Grabbed the Oak Room, sent the invite, and added
                              the agenda from last week&rsquo;s thread. 🎉
                            </p>
                            {/* Action receipt — a richer card that makes Giga's
                             reply feel like real, multi-step work: the booked
                             meeting on top, then a checklist of everything Giga
                             actually did (calendars cross-checked, room booked,
                             invites sent, agenda attached). Each row staggers in
                             so it reads as work completing, not a static stamp. */}
                            <motion.div className="mt-2.5 rounded-xl bg-[#FBEDE6] px-3 py-3" initial={reduced ? false : {
                          opacity: 0,
                          y: 8
                        }} animate={{
                          opacity: 1,
                          y: 0
                        }} transition={reduced ? {
                          duration: 0.2
                        } : {
                          delay: 0.45,
                          duration: 0.55,
                          ease: [0.22, 1, 0.36, 1]
                        }} data-id="element-602">
                            
                              <div className="flex items-center gap-2" data-id="element-603">
                                <CalendarCheckIcon size={18} strokeWidth={2.5} className="text-[#C2410C] shrink-0" data-id="element-604" />
                              
                                <div className="min-w-0" data-id="element-605">
                                  <span className="block text-sm font-semibold text-neutral-900 leading-tight" data-id="element-606">
                                    Team sync · Oak Room
                                  </span>
                                  <span className="block text-xs text-neutral-500 leading-tight" data-id="element-607">
                                    Thu, 2:00–2:30 PM · 6 going
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2.5 border-t border-[#C2410C]/15 pt-2.5 flex flex-col gap-1.5" data-id="element-608">
                                {['6 calendars cross-checked', 'Oak Room booked', 'Invite sent to the channel', 'Agenda attached from last thread'].map((item, i) => <motion.div key={item} className="flex items-center gap-1.5 text-xs font-semibold text-[#0F9D58]" initial={reduced ? false : {
                              opacity: 0,
                              x: -4
                            }} animate={{
                              opacity: 1,
                              x: 0
                            }} transition={reduced ? {
                              duration: 0.2
                            } : {
                              delay: 0.7 + i * 0.18,
                              duration: 0.3
                            }} data-id="element-609">
                                
                                    <CheckIcon size={14} strokeWidth={3} className="shrink-0" data-id="element-610" />
                                
                                    <span className="text-neutral-700 font-medium" data-id="element-611">
                                      {item}
                                    </span>
                                  </motion.div>)}
                              </div>
                            </motion.div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </div>

              {/* Slack composer — shows Marcus typing his follow-up before it's
                sent into the thread. */}
              <div className="px-4 pb-1.5 shrink-0" data-id="element-612">
                <div className="rounded-xl border border-neutral-200 bg-white px-3 pt-2.5 pb-2" data-id="element-613">
                  <p className="text-sm leading-snug min-h-[20px] text-neutral-700" data-id="element-614">
                    {!marcusSent ? <>
                        <span data-id="element-615">{MARCUS_TYPED.slice(0, typedLen)}</span>
                        {!reduced && <motion.span aria-hidden="true" className="inline-block w-px h-4 -mb-0.5 ml-px bg-[#EA580C] align-middle" animate={{
                      opacity: [1, 1, 0, 0]
                    }} transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear'
                    }} data-id="element-616" />}
                      </> : <span className="text-neutral-300" data-id="element-617">Message #team</span>}
                  </p>
                  <div className="mt-2 flex items-center justify-between" data-id="element-618">
                    <span className="flex items-center gap-3 text-neutral-300" data-id="element-619">
                      <PlusIcon size={18} strokeWidth={2.5} data-id="element-620" />
                      <SmileIcon size={18} strokeWidth={2.5} data-id="element-621" />
                    </span>
                    <motion.span className={`flex items-center justify-center w-8 h-8 rounded-lg ${typedLen > 0 && !marcusSent ? 'bg-[#007A5A]' : 'bg-neutral-200'}`} animate={reduced || marcusSent || typedLen === 0 ? undefined : {
                    scale: [1, 1.12, 1]
                  }} transition={reduced ? undefined : {
                    duration: 1.4,
                    repeat: Infinity,
                    ease: 'easeInOut'
                  }} data-id="element-622">
                      
                      <SendHorizonalIcon size={15} strokeWidth={2.5} className={typedLen > 0 && !marcusSent ? 'text-white' : 'text-neutral-400'} data-id="element-623" />
                      
                    </motion.span>
                  </div>
                </div>
              </div>

              {/* Home indicator */}
              <div className="pb-3 pt-1.5 flex justify-center bg-white shrink-0" data-id="element-624">
                <span className="h-1.5 w-32 rounded-full bg-neutral-900/80" data-id="element-625" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}
// "Connects in seconds" visual — modeled on the uploaded reference: full-color
// app tiles on soft rounded-white chips, scattered across the field riding on
// faint dashed orbital arcs that all sweep toward the BOTTOM-RIGHT corner,
// where the Giga mark sits as the orbit's anchor (every tool orbiting in to
// Giga). Tiles vary in size for depth and float gently. Business + social
// tools Giga plugs into.
const CONNECT_TILES = [
// size: tile px. Positions are tuned for an EVEN, airy scatter across the
// whole field (roughly three loose rows) with gentle size variation for
// depth — they still drift toward the Giga hub in the lower-right, but the
// field reads balanced rather than clumped in the upper-left. The bottom-
// right corner is intentionally left light because the Giga hub sits there.
// A broad mix of the real SaaS / business tools founders and teams run on.
// — top row —
{
  slug: 'asana',
  name: 'Asana',
  size: 50,
  top: '6%',
  left: '8%',
  rotate: -6,
  delay: 0.2
}, {
  slug: 'shopify',
  name: 'Shopify',
  size: 56,
  top: '2%',
  left: '40%',
  rotate: -4,
  delay: 0
}, {
  slug: 'stripe',
  name: 'Stripe',
  size: 46,
  top: '8%',
  left: '74%',
  rotate: 6,
  delay: 0.5
},
// — middle row —
{
  slug: 'gmail',
  name: 'Gmail',
  size: 44,
  top: '34%',
  left: '24%',
  rotate: 4,
  delay: 0.9
}, {
  slug: 'salesforce',
  name: 'Salesforce',
  size: 52,
  top: '38%',
  left: '54%',
  rotate: -5,
  delay: 1.2
}, {
  slug: 'notion',
  name: 'Notion',
  size: 44,
  top: '32%',
  left: '84%',
  rotate: 5,
  delay: 0.7
},
// — bottom row —
{
  slug: 'quickbooks',
  name: 'QuickBooks',
  size: 50,
  top: '64%',
  left: '6%',
  rotate: 6,
  delay: 0.3
}, {
  slug: 'googlesheets',
  name: 'Google Sheets',
  size: 46,
  top: '68%',
  left: '36%',
  rotate: -4,
  delay: 1.4
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  size: 44,
  top: '62%',
  left: '64%',
  rotate: 4,
  delay: 1.0
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  size: 42,
  top: '46%',
  left: '40%',
  rotate: -5,
  delay: 0.6
}];
function ConnectTile({
  tile,
  reduced
}: {
  tile: (typeof CONNECT_TILES)[number];
  reduced: boolean;
}) {
  const radius = tile.size >= 60 ? 18 : tile.size >= 50 ? 15 : 12;
  return <motion.div className="absolute bg-white shadow-[0_16px_34px_-14px_rgba(43,33,26,0.30)] ring-1 ring-black/[0.04] flex items-center justify-center" style={{
    top: tile.top,
    left: tile.left,
    width: tile.size,
    height: tile.size,
    borderRadius: radius,
    rotate: `${tile.rotate}deg`
  }} initial={{
    opacity: 0,
    scale: 0.6
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: tile.delay * 0.25,
    type: 'spring',
    stiffness: 220,
    damping: 17
  }} data-id="element-626">
      
      <motion.img src={`https://cdn.simpleicons.org/${tile.slug}`} alt={tile.name} className="object-contain" style={{
      width: tile.size * 0.52,
      height: tile.size * 0.52
    }} loading="lazy" onError={e => {
      const el = e.currentTarget as HTMLImageElement;
      if (!el.dataset.fallback) {
        el.dataset.fallback = '1';
        el.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tile.slug}.svg`;
      } else {
        el.style.visibility = 'hidden';
      }
    }} animate={reduced ? undefined : {
      y: [0, -6, 0]
    }} transition={reduced ? undefined : {
      duration: 4 + tile.delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: tile.delay
    }} data-id="element-627" />
      
    </motion.div>;
}
// The Giga mark anchored in the bottom-right corner — the orbital centre that
// every app tile arcs toward. Kept STATIC and flat: no perpetual float and no
// glow/halo, per request, so it simply sits as the solid hub of the
// constellation (it still fades/scales in once on first reveal).
function ConnectsHub({
  reduced
}: {
  reduced: boolean;
}) {
  return <motion.div className="absolute -bottom-3 -right-3 z-10 flex items-center justify-center" initial={{
    opacity: 0,
    scale: 0.5
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2,
    type: 'spring',
    stiffness: 200,
    damping: 16
  }} data-id="element-628">
      
      <div className="relative w-[68px] h-[68px] flex items-center justify-center" data-id="element-629">
        {/* The Giga flower on its own — no orange tile, no glow. The mark is
          filled with a FLAT brand orange (#EA580C, the same orange as the "Try
          Giga for free" buttons) using the PNG silhouette as a CSS mask, so it's
          a clean solid color with no gradient and no drop shadow. */}
        <span role="img" aria-label="Giga" className="w-12 h-12 bg-[#EA580C]" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-630" />
        
      </div>
    </motion.div>;
}
function ConnectsVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-631">
      {/* Concentric dashed orbital arcs the tiles ride on — anchored to the
                                                                                                                                                                  BOTTOM-RIGHT corner (the Giga hub) so every arc sweeps inward toward
                                                                                                                                                                  it, matching the reference's converging-orbit composition. */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-632">
        
        {[34, 54, 76, 98].map((r, i) => <circle key={r} cx="100" cy="104" r={r} fill="none" stroke="#EA580C" strokeOpacity={0.38 - i * 0.05} strokeWidth="0.9" strokeDasharray="1.6 2.4" vectorEffect="non-scaling-stroke" data-id="element-633" />)}
      </svg>
      {/* The absolutely-positioned tiles are laid out in % across the card. On
                                                                                                                                    a narrow mobile card those fixed-px tiles crowd and collide, so we scale
                                                                                                                                    the whole constellation down a touch on small screens (origin top-center)
                                                                                                                                    so it stays airy and uncluttered; full size from sm up. */}
      <div className="absolute inset-0 origin-top scale-[0.82] sm:scale-100" data-id="element-634">
        {CONNECT_TILES.map(tile => <ConnectTile key={tile.slug} tile={tile} reduced={reduced} data-id="element-635" />)}
      </div>
      <ConnectsHub reduced={reduced} data-id="element-636" />
    </div>;
}
// "Acts like a partner" visual — a DENSE pill-cloud (modeled on the uploaded
// reference layout): a field of faint grey ghost pills (the endless busy-work)
// tilted and bleeding off the card edges, with three vivid accent pills
// floating in front — the work Giga actually picks up. The accent pills use
// ONLY Giga's palette (warm orange family + dark brown), not the reference's
// purple/pink/green, which aren't part of our system.
const GHOST_PILLS = [{
  text: 'tips',
  top: '6%',
  left: '40%',
  rotate: -4
}, {
  text: 'content',
  top: '2%',
  left: '72%',
  rotate: 5
}, {
  text: 'goals',
  top: '20%',
  left: '-8%',
  rotate: -7
}, {
  text: 'reals',
  top: '24%',
  left: '74%',
  rotate: 6
}, {
  text: 'statistics',
  top: '44%',
  left: '-6%',
  rotate: 4
}, {
  text: 'research',
  top: '46%',
  left: '82%',
  rotate: -5
}, {
  text: 'new ideas',
  top: '74%',
  left: '14%',
  rotate: -4
}, {
  text: 'captions',
  top: '70%',
  left: '-10%',
  rotate: 6
}, {
  text: 'recaps',
  top: '88%',
  left: '64%',
  rotate: 5
}];
const ACCENT_PILLS = [{
  text: 'replies',
  top: '20%',
  left: '14%',
  rotate: -6,
  bg: 'bg-[#F59E5B]',
  delay: 0
}, {
  text: 'nudges',
  top: '42%',
  left: '32%',
  rotate: 5,
  bg: 'bg-[#EA580C]',
  delay: 0.12
}, {
  text: 'notices',
  top: '64%',
  left: '46%',
  rotate: -5,
  bg: 'bg-[#C2410C]',
  delay: 0.24
}];
function PartnerVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-637">
      {/* The pill cloud is laid out with absolute % positions tuned for the
                                                                                                                                    desktop card. On a narrow mobile card the bigger accent pills overlap
                                                                                                                                    hard and a few ghost pills bleed messily off-frame, so we scale the
                                                                                                                                    whole cloud down a touch on small screens (origin top-center) to keep
                                                                                                                                    it legible and tidy; full size from sm up. */}
      <div className="absolute inset-0 origin-top scale-[0.8] sm:scale-100" data-id="element-638">
        {/* Ghost layer — dense, faint, tilted pills, several bleeding past the
          card edges so the cloud feels deeper and busier than the frame. */}
        {GHOST_PILLS.map((pill, i) => <motion.span key={pill.text} className="absolute whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold text-neutral-300 bg-neutral-100" style={{
        top: pill.top,
        left: pill.left,
        rotate: `${pill.rotate}deg`
      }} initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.04,
        duration: 0.4
      }} data-id="element-639">
          
            {pill.text}
          </motion.span>)}
        {/* Accent layer — the vivid pills Giga picks up: larger, bolder, and
          floating above the ghost field. */}
        {ACCENT_PILLS.map(pill =>
      // The whole pill floats; the text inside stays put relative to it.
      // The continuous bob lives on a dedicated wrapper so it doesn't fight
      // the entrance spring (initial/whileInView) on the same element.
      <motion.span key={pill.text} className="absolute z-10" style={{
        top: pill.top,
        left: pill.left
      }} initial={{
        opacity: 0,
        scale: 0.7,
        y: 12
      }} whileInView={{
        opacity: 1,
        scale: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3 + pill.delay,
        type: 'spring',
        stiffness: 240,
        damping: 18
      }} data-id="element-640">
          
            <motion.span className={`block whitespace-nowrap px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-base sm:text-lg font-bold text-white shadow-[0_16px_34px_-12px_rgba(43,33,26,0.45)] ${pill.bg}`} style={{
          rotate: `${pill.rotate}deg`
        }} animate={reduced ? undefined : {
          y: [0, -5, 0]
        }} transition={reduced ? undefined : {
          duration: 4 + pill.delay * 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-641">
            
              {pill.text}
            </motion.span>
          </motion.span>)}
      </div>
    </div>;
}
// "Follows through" visual — a continuous left→right carousel of REAL-LOOKING
// deliverable screenshots (a spreadsheet, an analytics chart, a finished-task
// toast, a generated PDF report, and a built slide deck). Each is a tidy little
// app window so the row reads like a stream of finished work drifting by, which
// feels livelier than the old fanned, slowly-floating stack.
function WindowDots() {
  return <span className="flex items-center gap-1 shrink-0" data-id="element-642">
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" data-id="element-643" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" data-id="element-644" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#28C840]" data-id="element-645" />
    </span>;
}
// Each deliverable card is a fixed-size little app window so the carousel row
// stays even. Built once and reused across the marquee loop.
const FT_CARD_BASE = 'w-[156px] h-[176px] shrink-0 rounded-xl bg-white border border-neutral-200 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.35)] overflow-hidden flex flex-col';
// "An email sent" — a sent-mail confirmation. Reads instantly as a concrete,
// finished task: Giga wrote and sent a real email on your behalf.
function EmailCard() {
  return <div className={FT_CARD_BASE} data-id="element-646">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-647">
        <MailIcon size={10} className="text-white shrink-0" strokeWidth={2.5} data-id="element-648" />
        <span className="text-[8px] font-bold text-white truncate" data-id="element-649">
          Email sent
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-650">
        <div className="flex items-center gap-1 text-[7px] text-neutral-400 mb-1" data-id="element-651">
          <span className="font-semibold text-neutral-500" data-id="element-652">To:</span>
          sarah@acmecorp.com
        </div>
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-653">
          Re: Q3 invoice &amp; next steps
        </div>
        <div className="space-y-1" data-id="element-654">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-655" />
          <div className="h-1 w-[94%] rounded bg-neutral-100" data-id="element-656" />
          <div className="h-1 w-2/3 rounded bg-neutral-100" data-id="element-657" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-658">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-659">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-660" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-661">
            Delivered · just now
          </span>
        </div>
      </div>
    </div>;
}
// "A blog post published" — a finished, recognizable creative deliverable.
// Giga drafted the post and pushed it live, end to end.
function BlogCard() {
  return <div className={FT_CARD_BASE} data-id="element-662">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-663">
        <FileTextIcon size={10} className="text-white shrink-0" strokeWidth={2.5} data-id="element-664" />
        
        <span className="text-[8px] font-bold text-white truncate" data-id="element-665">
          Blog published
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-666">
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-667">
          How we cut onboarding to 5 minutes
        </div>
        <div className="space-y-1" data-id="element-668">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-669" />
          <div className="h-1 w-[92%] rounded bg-neutral-100" data-id="element-670" />
          <div className="h-1 w-3/4 rounded bg-neutral-100" data-id="element-671" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-672">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-673">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-674" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-675">
            Live on the blog
          </span>
        </div>
      </div>
    </div>;
}
function ToastCard() {
  return <div className={`${FT_CARD_BASE} justify-center px-3`} data-id="element-676">
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0F9D58] shrink-0 mb-2.5" data-id="element-677">
        <CheckIcon size={18} strokeWidth={3.5} className="text-white" data-id="element-678" />
      </span>
      <div className="text-[11px] font-bold text-neutral-900 leading-tight" data-id="element-679">
        Domain connected
      </div>
      <p className="mt-1 text-[9.5px] text-neutral-500 leading-snug" data-id="element-680">
        getgiga.com is live — DNS verified and SSL issued.
      </p>
      <div className="mt-2 flex items-center gap-1" data-id="element-681">
        <span className="w-1 h-1 rounded-full bg-[#0F9D58]" data-id="element-682" />
        <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-683">
          Completed just now
        </span>
      </div>
    </div>;
}
// "Finances reconciled" — a recognizable back-office outcome: Giga matched the
// month's transactions and balanced the books, end to end.
function FinanceCard() {
  return <div className={FT_CARD_BASE} data-id="element-684">
      <div className="flex items-center justify-between px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-685">
        <WindowDots data-id="element-686" />
        <span className="text-[7px] font-bold text-neutral-400" data-id="element-687">
          Bookkeeping
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-688">
        <div className="text-[10px] font-bold text-neutral-900 leading-tight mb-2" data-id="element-689">
          Finances reconciled
        </div>
        <div className="space-y-1.5" data-id="element-690">
          {[{
          label: 'Transactions matched',
          value: '142'
        }, {
          label: 'Flagged for review',
          value: '0'
        }].map(r => <div key={r.label} className="flex items-center justify-between gap-2" data-id="element-691">
            
              <span className="text-[8px] text-neutral-500 truncate" data-id="element-692">
                {r.label}
              </span>
              <span className="text-[8px] font-bold text-neutral-800 shrink-0" data-id="element-693">
                {r.value}
              </span>
            </div>)}
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-694">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-695">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-696" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-697">
            Balanced · August
          </span>
        </div>
      </div>
    </div>;
}
// "A meeting booked" — a calendar invite Giga scheduled and confirmed. Another
// concrete, everyday outcome people instantly understand.
function MeetingCard() {
  return <div className={FT_CARD_BASE} data-id="element-698">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-699">
        <CalendarCheckIcon size={10} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-700" />
        
        <span className="text-[8px] font-bold text-neutral-500 truncate" data-id="element-701">
          Calendar
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-702">
        <div className="flex items-center gap-2 mb-2" data-id="element-703">
          <div className="flex flex-col items-center justify-center w-9 h-9 rounded-md bg-[#EA580C]/10 shrink-0" data-id="element-704">
            <span className="text-[6px] font-bold text-[#C2410C] uppercase leading-none" data-id="element-705">
              Thu
            </span>
            <span className="text-[13px] font-bold text-[#C2410C] leading-none" data-id="element-706">
              14
            </span>
          </div>
          <div className="min-w-0" data-id="element-707">
            <div className="text-[10px] font-bold text-neutral-900 leading-tight truncate" data-id="element-708">
              Demo with Acme
            </div>
            <div className="text-[8px] text-neutral-400" data-id="element-709">2:00 – 2:30 PM</div>
          </div>
        </div>
        <div className="flex items-center gap-1 mb-2" data-id="element-710">
          {['#EA580C', '#0F9D58', '#2B211A'].map(c => <span key={c} className="w-3.5 h-3.5 rounded-full border border-white" style={{
          backgroundColor: c
        }} data-id="element-711" />)}
          <span className="text-[7px] text-neutral-400 ml-0.5" data-id="element-712">+2 invited</span>
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-713">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-714">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-715" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-716">
            Invites sent
          </span>
        </div>
      </div>
    </div>;
}
function FollowsThroughVisual() {
  const reduced = !!useReducedMotion();
  const cards = [<EmailCard key="email" data-id="element-717" />, <BlogCard key="blog" data-id="element-718" />, <ToastCard key="toast" data-id="element-719" />, <FinanceCard key="finance" data-id="element-720" />, <MeetingCard key="meeting" data-id="element-721" />];
  return <div className="flex-1 relative mt-6 overflow-hidden flex items-center" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)'
  }} data-id="element-722">
      
      {reduced ?
    // Reduced motion: a static, horizontally-scrollable row.
    <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-723">
          {cards}
        </div> :
    // Continuous left→right marquee: the track holds two identical sets and
    // animates from -50% to 0% so finished-work cards drift rightward in a
    // seamless loop.
    <motion.div className="flex gap-4 w-max" animate={{
      x: ['-50%', '0%']
    }} transition={{
      duration: 26,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-724">
        
          {[...cards, ...cards].map((card, i) => cloneElement(card, {
        key: `ft-${i}`
      }))}
        </motion.div>}
    </div>;
}
// "Remembers everything" visual — a Slack thread where a teammate asks Giga to
// recall a specific past conversation, and Giga replies having remembered the
// exact details (a recalled checklist), proving context carries over.
const RECALLED = ['The $40k annual cap we agreed on', 'Net-45 payment terms', 'Onboarding starts in Q1'];
function RemembersVisual() {
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-725">
      <div className="absolute inset-0 flex items-stretch justify-center" data-id="element-726">
        {/* Compact Slack window */}
        <div className="w-full max-w-[280px] self-center rounded-2xl bg-white border border-neutral-200 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.3)] overflow-hidden" data-id="element-727">
          {/* Slack channel header (aubergine) */}
          <div className="bg-[#3F0E40] px-3 py-2 flex items-center gap-1.5" data-id="element-728">
            <HashIcon size={11} className="text-white/70 shrink-0" data-id="element-729" />
            <span className="text-[11px] font-bold text-white truncate" data-id="element-730">
              acme-deal
            </span>
          </div>
          <div className="p-3 space-y-3" data-id="element-731">
            {/* Teammate asks Giga to recall the earlier conversation */}
            <motion.div className="flex items-start gap-2" initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4
          }} data-id="element-732">
              
              <img src="/CleanShot_2026-06-22_at_12.15.002x.png" alt="Faith" className="w-6 h-6 rounded-md object-cover shrink-0" loading="lazy" data-id="element-733" />
              
              <div className="min-w-0" data-id="element-734">
                <div className="flex items-baseline gap-1.5" data-id="element-735">
                  <span className="text-[11px] font-bold text-neutral-900 leading-none" data-id="element-736">
                    Faith
                  </span>
                  <span className="text-[9px] text-neutral-400 leading-none" data-id="element-737">
                    10:02
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-neutral-700 leading-snug" data-id="element-738">
                  <span className="font-semibold text-[#1264A3] bg-[#1264A3]/10 rounded px-0.5" data-id="element-739">
                    @Giga
                  </span>{' '}
                  remember what we agreed with Acme last week? Draft the
                  follow-up.
                </p>
              </div>
            </motion.div>

            {/* Giga replies, having recalled the specifics */}
            <motion.div className="flex items-start gap-2" initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: 0.35
          }} data-id="element-740">
              
              <span className="w-6 h-6 rounded-md bg-[#EA580C] flex items-center justify-center shrink-0" data-id="element-741">
                <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="w-3.5 h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-742" />
                
              </span>
              <div className="min-w-0 flex-1" data-id="element-743">
                <div className="flex items-baseline gap-1.5" data-id="element-744">
                  <span className="text-[11px] font-bold text-neutral-900 leading-none" data-id="element-745">
                    Giga
                  </span>
                  <span className="text-[8px] font-bold text-[#616061] bg-neutral-200/80 rounded px-1 py-0.5 leading-none uppercase tracking-wide" data-id="element-746">
                    App
                  </span>
                </div>
                <div className="mt-1.5 rounded-lg border border-neutral-200 bg-[#FAF8F4] p-2.5" data-id="element-747">
                  <div className="flex items-center gap-1 text-[9px] font-bold text-[#EA580C] uppercase tracking-wide mb-1.5" data-id="element-748">
                    <Sparkles size={9} data-id="element-749" /> Recalled from last week
                  </div>
                  <ul className="space-y-1" data-id="element-750">
                    {RECALLED.map((item, i) => <motion.li key={item} className="flex items-start gap-1.5 text-[10px] text-neutral-700 leading-snug" initial={{
                    opacity: 0,
                    x: -6
                  }} whileInView={{
                    opacity: 1,
                    x: 0
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.3,
                    delay: 0.6 + i * 0.12
                  }} data-id="element-751">
                      
                        <CheckIcon size={11} strokeWidth={3} className="text-[#0F9D58] shrink-0 mt-px" data-id="element-752" />
                      
                        <span data-id="element-753">{item}</span>
                      </motion.li>)}
                  </ul>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>;
}
export function BenefitsBento() {
  return <section className="w-full bg-[#F4F0EA] py-24 md:py-32 border-y border-neutral-200/70 overflow-hidden" data-id="element-754">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-755">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20" data-id="element-756">
          {/* Eyebrow — a pulsing live dot next to an "early access" label, to
            signal the product is invite-only / coming soon. */}
          <span className="inline-flex items-center gap-2.5 mb-5 md:mb-6 rounded-full border border-[#EA580C]/20 bg-[#EA580C]/[0.06] pl-2.5 pr-3.5 py-1.5" data-id="element-757">
            <span className="relative flex h-2 w-2 shrink-0" data-id="element-758">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#EA580C] opacity-75" data-id="element-759" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#EA580C]" data-id="element-760" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#C2410C]" data-id="element-761">
              Get Early Access
            </span>
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-762">
            Meet your most capable hire
          </h2>
          <p className="text-lg md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-763">
            A proactive multiplayer agent that connects to all your tools, works
            in your workspace, and executes with all the context.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-764">
          {/* Card 1: Works where you already are */}
          <div className="md:col-span-2 bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 md:p-12 border border-neutral-200/60 shadow-sm flex flex-col md:flex-row items-center gap-5 md:gap-8 overflow-hidden" data-id="element-765">
            <div className="flex-1 text-center md:text-left" data-id="element-766">
              <h3 className="text-xl sm:text-2xl md:text-3xl font-title font-medium text-[#2B211A] mb-3" data-id="element-767">
                {/* Shorter copy on mobile, fuller copy from the sm breakpoint up. */}
                <span className="sm:hidden" data-id="element-768">Works where you work</span>
                <span className="hidden sm:inline" data-id="element-769">
                  Works where you already are
                </span>
              </h3>
              <p className="text-neutral-500 text-base sm:text-lg" data-id="element-770">
                Giga lives in Slack and soon Teams, your messaging apps, and
                your email.
              </p>
            </div>
            <div className="w-full shrink-0 md:flex-1 relative h-[340px] sm:h-[380px] rounded-2xl overflow-hidden flex justify-center items-center border border-neutral-100" data-id="element-771">
              {/* Grainy orange/dark gradient backdrop — a portion of the
                uploaded texture so the phone sits on warm Giga atmosphere. */}
              <img src="/Untitled_design_(6)_1.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" style={{
              objectPosition: '60% 50%'
            }} loading="lazy" data-id="element-772" />
              
              {/* Gentle dark vignette so the white phone pops off the texture. */}
              <div aria-hidden="true" className="absolute inset-0" style={{
              background: 'radial-gradient(70% 70% at 50% 70%, transparent 0%, rgba(43,33,26,0.18) 100%)'
            }} data-id="element-773" />
              
              {/* Bottom half of an iPhone with a live Slack scene. */}
              <WorksWhereYouWorkVisual data-id="element-774" />
            </div>
          </div>

          {/* Card 2: Connects in seconds */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-775">
            <div className="text-center md:text-left relative z-20" data-id="element-776">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-777">
                Self onboards in seconds
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-778">
                Once connected, Giga gets up to speed.
              </p>
            </div>
            <ConnectsVisual data-id="element-779" />
          </div>

          {/* Card 3: Acts like a partner */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-780">
            <div className="text-center md:text-left relative z-20" data-id="element-781">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-782">
                Acts on your behalf
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-783">
                Giga doesn't just reply — it does the work and hands back real
                results.
              </p>
            </div>
            <PartnerVisual data-id="element-784" />
          </div>

          {/* Card 4: Follows through */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-785">
            <div className="text-center md:text-left relative z-20" data-id="element-786">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-787">
                Never out of office
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-788">
                A colleague who's always on — replying, nudging, and noticing,
                even when you're not.
              </p>
            </div>
            <FollowsThroughVisual data-id="element-789" />
          </div>

          {/* Card 5: Remembers everything */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-790">
            <div className="text-center md:text-left relative z-20" data-id="element-791">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-792">
                Remembers everything
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-793">
                Context carries over so you never have to explain twice.
              </p>
            </div>
            <RemembersVisual data-id="element-794" />
          </div>
        </div>
      </div>
    </section>;
}